/* eslint-disable react/jsx-filename-extension */
/**
 * This page _app.js is loaded automatically by Next.js.
 */
import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ErrorBoundary } from 'react-error-boundary';
import App from 'next/app';
import Head from 'next/head'; // https://github.com/vercel/next.js/issues/9647#issuecomment-583844001

import ErrorFallbackContent from 'Components/ErrorFallbackContent/ErrorFallbackContent';
import { AppProviders } from 'Context/AppProviders';
import { ALL_FEATURES_RELATED_COOKIES, FEATURES } from 'Context/FeaturesContext/FeaturesContextConsts';
import { getFeatureTogglesFromEnv } from 'Context/FeaturesContext/featureToggle.helpers';
import { initDatadogBrowserLogs } from 'Services/datadog/datadogLogs';
import { initDatadogRum } from 'Services/datadog/datadogRum';
import { initGAUserAndRecordInitialPageView, setGAUser } from 'Services/gtm';
import { logger } from 'Services/logger/logger';
import { trackSnowplowPageView } from 'Services/snowplow';
import useRouterStore from 'Stores/RouterStore/RouterStore';
import { LIST_TYPES, NO_SCROLL_TO_TOP_ROUTES } from 'Utilities/consts';
import { initialiseDayjs } from 'Utilities/dayjs';
import { handleReactErrorBoundaryError } from 'Utilities/error/handleReactErrorBoundaryError';
import fetchAppWideData from 'Utilities/fetchers/fetchAppWideData';
import { handleRefreshIfOutOfSyncBuild } from 'Utilities/handleRefreshIfOutOfSyncBuild';
import { getIsomorphicCookie, isDev, isProd, stringBooleanToBoolean } from 'Utilities/helpers';
import isStaging from 'Utilities/helpers/isStaging';
import { getInitialListType } from 'Utilities/helpers/listType.helpers';
import { handleWebVitals } from 'Utilities/helpers/webVitals';
import { IGNORED_FROM_HISTORY_ROUTES, SCROLL_TOP_USING_AS_PATH_CHANGE_ROUTES } from 'Utilities/routes';
import { coerceQueryBooleans } from 'Utilities/ssr/coerceQueryBooleans';
import { handleWindowCommunications } from 'Utilities/windowCommunicationChannel/windowCommunicationChannel.helpers';

import 'Utilities/styles/global.scss';
import 'Utilities/styles/main.scss';

// CAUTION: import doesn't work
// @ref: https://github.com/zeit/next-plugins/issues/266#issuecomment-422562693
require('../node_modules/@motorway/motorway-component-library/dist/index.css');
require('../node_modules/@motorway/motorway-storybook-cra/dist/main.css');
// eslint-disable-next-line import/no-unresolved,import/extensions
require('../node_modules/@motorway/mw-dealer-shared-components/dist/components/index.css');
require('../node_modules/react-popper-tooltip/dist/styles.css');

require('../node_modules/swiper/swiper-bundle.min.css');

initialiseDayjs();

export const reportWebVitals = (metric) => {
	const isWebVitalsLoggingEnabled = stringBooleanToBoolean(getIsomorphicCookie(FEATURES.webVitalsLogging));
	if (isWebVitalsLoggingEnabled) {
		handleWebVitals(metric);
	}
};

export default class Pro extends App {
	constructor(...props) {
		super(...props);

		this.state = {
			hasError: false,
			windowError: undefined,
		};

		const { router, user } = this.props;

		this.windowLocationHref = router?.asPath ?? null;

		if (user) {
			setGAUser(user);
		}
	}

	componentDidMount() {
		const isDatadogRumEnabled = this.props.cookies?.[FEATURES.datadogRum];
		const isDatadogRumTracingEnabled = this.props.cookies?.[FEATURES.datadogRumTracing];

		const { release, user } = this.props;

		if (isDatadogRumEnabled && (isStaging || isProd)) {
			initDatadogRum({ isDatadogRumTracingEnabled, release, user });
		}

		initDatadogBrowserLogs({ release, user });
		initGAUserAndRecordInitialPageView();
		handleWindowCommunications();

		if (!isProd) {
			// eslint-disable-next-line no-console
			const featureManagerLabel = 'Enable feature manager';
			/* eslint-disable no-console */
			console.groupCollapsed(featureManagerLabel);
			const enableFeatureManager = window.cookieStore
				? 'document.cookie="mwDisplayFeaturesManager=true; path=/";'
				: '(function(){document.cookie="mwDisplayFeaturesManager=true; path=/";window.location.reload();})();';

			console.info(
				`To enable feature manager paste the code below into the console: %c${enableFeatureManager}`,
				'background: #1D1D1B; color: #E5F4FB; font-family: Courier; padding: 4px 8px; border-radius: 4px; border: solid 1px #4A4A48; margin: 8px 0;',
			);
			console.groupEnd(featureManagerLabel);
			/* eslint-enable no-console */
		}

		// @ref https://github.com/zeit/next.js/issues/3303#issuecomment-354225323
		window.history.scrollRestoration = 'manual';

		const { router } = this.props;
		if (!router) {
			return;
		}

		this.prevRoute = router.route;
		this.prevRouteAsPath = router?.asPath;
		this.prevQuery = router?.query;
		router.events.on('routeChangeComplete', this.routeChange);

		window.onerror = (_message, _source, _lineno, _colno, error) => {
			this.setState({ windowError: error });
		};

		window.onunload = () => {
			this.setState({ hasError: false, windowError: undefined });
		};
	}

	// Establishes a per-request CLS context on the server so that resolveUser (which
	// seeds the identity JWT) and fetchAppWideData (which calls dealer-core via
	// dataProvider's genericInterceptor) share one AsyncLocalStorage store. Without this,
	// getInitialProps pages made dealer-core calls with no `x-mway-user` header →
	// `401 'x-mway-user' header required` (parity with the old custom server's per-request
	// CLS middleware). Dynamically imported so async_hooks never reaches the client bundle.
	static async getInitialProps(appContext) {
		const { ctx } = appContext;

		if (ctx?.req && typeof window === 'undefined') {
			const { runInRequestContext } = await import('Utilities/ssr/withRequestContext');
			return runInRequestContext(ctx.req, () => Pro.buildInitialProps(appContext));
		}

		return Pro.buildInitialProps(appContext);
	}

	// The actual work of App.getInitialProps. Kept as a separate method so
	// getInitialProps can run it inside a per-request CLS context on the server (see below).
	static async buildInitialProps({ Component, ctx }) {
		const isSSR = Boolean(ctx.req);

		if (isSSR && ctx.query && typeof ctx.query === 'object') {
			ctx.query = coerceQueryBooleans(ctx.query);
		}

		const getIsomorphicUser = async () => {
			try {
				/**
				 * We KNOW that NextJS hydrates data to FE using window.__NEXT_DATA__.
				 * Hydration always starts from server, which, if user is logged in
				 * will add user into propsFromServer object, which then is merged into context
				 * and send to frontend.
				 *
				 * This means that any browser run of getInitialProps WILL have access to
				 * window.__NEXT_DATA__, which means we can check for user in it!
				 */

				if (isSSR) {
					// If a page's getServerSideProps (via withAuth) already resolved the user,
					// it will be attached to ctx.req.user. Otherwise resolve it here.
					if (ctx.req.user) {
						return ctx.req.user;
					}
					// Dynamic import is safe here because getServerSideProps/getInitialProps
					// server path is tree-shaken from client bundle by Next.js when using
					// typeof window check.
					if (typeof window === 'undefined') {
						const { resolveUser } = await import('Auth/resolveUser');
						return resolveUser(ctx.req, ctx.res);
					}
				}

				// eslint-disable-next-line no-underscore-dangle
				return window.__NEXT_DATA__.props.user;
			} catch (error) {
				const context = { req: ctx.req || {}, res: ctx.res || {} };

				logger.error({ context, error, message: 'Failed to load hydrate user', scope: 'App getIsomorphicUser' });

				return undefined;
			}
		};

		const user = await getIsomorphicUser();

		// getInitialProps-based pages read auth from `req.user` (via their auth
		// middleware / isServerSideAuth), which the old Passport custom server set on
		// every request. On the native runtime we mirror that here so those pages see
		// the resolved user during SSR.
		if (isSSR && ctx.req && user) {
			ctx.req.user = user;
		}

		// Seed the auth token + identity JWT into THIS request's CLS store so that
		// fetchAppWideData and the page's getInitialProps SSR service calls (via
		// createAxiosInstanceForService, which reads the Bearer token from CLS) are
		// authenticated. resolveUser is the usual seeder, but getIsomorphicUser
		// early-returns without calling it whenever ctx.req.user was already resolved by
		// a page's withAuth (which seeds a DIFFERENT, sibling CLS store). Without this,
		// _app's store is unseeded and every upstream call 403s with
		// "No access token found in cls storage". Server-only (CLS is async_hooks-backed).
		if (isSSR && typeof window === 'undefined' && user) {
			const { seedAuthCls } = await import('Auth/resolveUser');
			seedAuthCls(ctx.req, user);
		}

		// Transitional compatibility shim: the old custom Express server gave handlers
		// an Express response with `res.redirect([status], url)`. Many pages/middlewares
		// still use `getInitialProps` and call `res.redirect(...)`. On the native runtime
		// `ctx.res` is a plain Node `ServerResponse` without `.redirect`, which throws
		// (`context.res?.redirect is not a function`). Polyfill it here so every
		// getInitialProps page is unblocked until it migrates to getServerSideProps.
		if (isSSR && ctx.res && typeof ctx.res.redirect !== 'function') {
			ctx.res.redirect = function redirect(statusOrUrl, maybeUrl) {
				const hasStatus = typeof maybeUrl !== 'undefined';
				const statusCode = hasStatus ? statusOrUrl : 302;
				const location = hasStatus ? maybeUrl : statusOrUrl;

				if (!this.headersSent) {
					this.statusCode = statusCode;
					this.setHeader('Location', location);
					this.end();
				}

				return this;
			};
		}

		// Parity with the old custom server's global `getFeatureToggles` middleware
		// (req.features on every request) and per-request cache policy.
		// getServerSideProps pages get these via withAuth; getInitialProps pages get them
		// here. Authenticated responses are never cached. Anonymous legacy pages retain
		// the previous public cache policy unless a route wrapper has already installed a
		// stricter policy (for example, a no-store auth redirect).
		if (isSSR && ctx.req) {
			ctx.req.features = getFeatureTogglesFromEnv({
				dealerId: user?.dealerId,
				envString: process.env.FEATURE_TOGGLES,
			});
		}

		if (isSSR && ctx.res && !ctx.res.headersSent) {
			if (user) {
				ctx.res.setHeader('Cache-Control', 'no-cache, no-store, must-revalidate');
			} else if (!ctx.res.getHeader('Cache-Control')) {
				ctx.res.setHeader('Cache-Control', 'public, max-age=3600');
			}

			const priorHeaderValue = ctx.res.getHeader('Vary');
			const varyValues = (Array.isArray(priorHeaderValue) ? priorHeaderValue : [priorHeaderValue])
				.filter((value) => typeof value !== 'undefined')
				.flatMap((value) => String(value).split(','))
				.map((value) => value.trim())
				.filter(Boolean);

			if (!varyValues.some((value) => value.toLowerCase() === 'cookie')) {
				varyValues.push('Cookie');
			}

			ctx.res.setHeader('Vary', varyValues.join(', '));
		}

		const cookies = ALL_FEATURES_RELATED_COOKIES.reduce(
			(acc, cookieName) => ({
				...acc,
				[cookieName]: getIsomorphicCookie(cookieName, ctx.req) === 'true',
			}),
			{},
		);

		const isMirageEnabled = cookies?.[FEATURES.mirageServer];

		const pageContext = Object.assign(ctx, { isDev, isSSR, user });
		// appWideData is only available on server side, context values on other pages will have
		// undefined values for makes, models and savedFilters on client side routing.
		const appWideData = isSSR && user ? await fetchAppWideData(pageContext, isMirageEnabled) : {};

		const context = Object.assign(pageContext, { ...appWideData });

		try {
			const pageProps = Component.getInitialProps ? await Component.getInitialProps(context) : {};

			const isRemoveBuyItNowFeatureEnabled = cookies?.[FEATURES.removeBuyItNow];

			const initialListType =
				isRemoveBuyItNowFeatureEnabled &&
				context.query?.listType !== LIST_TYPES.shortlist &&
				context.query?.listType !== LIST_TYPES.aiCompanion
					? LIST_TYPES.auction
					: getInitialListType({
							listType: context.query?.listType,
							saleTimes: appWideData.saleTimes,
						});

			return {
				env: process.env.NODE_ENV,
				listType: initialListType,
				pageProps,
				release: process.env.BUILD_ID,
				user,
				...appWideData,
				cookies,
			};
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('getInitialProps ERROR:', error);
			logger.error({ context, error, message: 'Failed to define page props', scope: 'App getInitialProps' });

			return { hasError: true };
		}
	}

	static getDerivedStateFromProps(props, state) {
		// If there was an error generated within getInitialProps, and we haven't
		// yet seen an error, we add it to this.state here
		return {
			hasError: props.hasError || state.hasError || false,
		};
	}

	static getDerivedStateFromError() {
		// React Error Boundary here allows us to set state flagging the error (and
		// later render a fallback UI).
		return { hasError: true };
	}

	// eslint-disable-next-line class-methods-use-this
	componentDidCatch(error, errorInfo) {
		logger.error({
			context: errorInfo,
			error,
			message: 'Error caught on App component',
			scope: 'App componentDidCatch',
		});
	}

	componentWillUnmount() {
		const { router } = this.props;

		router?.events?.off?.('routeChangeComplete', this.routeChange);
	}

	routeChange = () => {
		const { release, router } = this.props;
		const { pathname, search } = window.location;

		const windowUrl = `${pathname}${search}`;

		if (this.windowLocationHref !== windowUrl) {
			trackSnowplowPageView();
		}

		this.windowLocationHref = windowUrl;

		if (!router) {
			return;
		}

		handleRefreshIfOutOfSyncBuild({ clientBuildId: release, route: router.state.route });

		const isNewRoute =
			(router.route !== this.prevRoute && !IGNORED_FROM_HISTORY_ROUTES.includes(this.prevRoute)) ||
			(SCROLL_TOP_USING_AS_PATH_CHANGE_ROUTES.includes(router.route) && router.asPath !== this.prevRouteAsPath);

		if (isNewRoute) {
			useRouterStore.setState({
				prevQuery: this.prevQuery,
				prevRoute: this.prevRoute,
				prevRouteAsPath: this.prevRouteAsPath,
			});
		}

		// reset scroll to top on each route transition
		if (isNewRoute && !NO_SCROLL_TO_TOP_ROUTES.includes(router.route)) {
			window.scrollTo(0, 0);
		}

		this.prevRoute = router.route;
		this.prevRouteAsPath = router?.asPath;
		this.prevQuery = router?.query;
	};

	render() {
		const content = this.state.hasError ? (
			<ErrorFallbackContent state={this.state?.windowError} />
		) : (
			<AppProviders {...this.props}>{super.render()}</AppProviders>
		);

		return (
			<ErrorBoundary
				FallbackComponent={() => <ErrorFallbackContent state={this.state} />}
				onError={handleReactErrorBoundaryError}
				onReset={() => {
					this.setState({ hasError: false, windowError: undefined });
				}}
			>
				<Head>
					<meta content="width=device-width,initial-scale=1,shrink-to-fit=no,viewport-fit=cover" name="viewport" />
				</Head>
				{content}
			</ErrorBoundary>
		);
	}
}
